import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import InvitationMessage, {
  InvalidInvitationMessageError,
} from "legal-entities/invitations/messages/invitation-message";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

const MessageField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

type MessageInputProps = {
  idPrefix?: string;
  className?: string;
  initialValue?: InvitationMessage;
  /**
   * This method is called when the name instance is modified. This callback is only
   * registered initially, to reregister the handler, change the key
   * @returns ({@link InvitationMessage}, undefined) when successful
   * @returns (undefined, errorMessage: string) when unsuccessful
   */
  onChange?: (message?: InvitationMessage, error?: string) => void;
};

export default function InvitationMessageInput(props: Readonly<MessageInputProps>) {
  const { idPrefix, className, initialValue, onChange } = props;

  const [messageText, setMessageText] = useState<string>(initialValue?.value ?? "");
  const [messageError, setMessageError] = useState<string>("");

  const messageIsDirty = useRef<boolean>(false);

  const debouncedValidate = useMemo(() => 
    debounce((messageString: string, isDirty?: boolean) => {
      let newMessage: InvitationMessage | undefined;
      let messageFieldError: string = "";

      if(!isDirty) return;
      try {
        newMessage = messageString ? new InvitationMessage(messageString) : undefined;
      } catch (error) {
        if (error instanceof InvalidInvitationMessageError) {
          messageFieldError = error.message;
          return;
        }
        throw error;
      } finally {
        setMessageError(messageFieldError);
        onChange?.(newMessage, messageFieldError);
      }
    }, 300),
  // We cannot guarantee onChange being stable, if it changes, change the key to recreate the component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    debouncedValidate(messageText, messageIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [messageText, debouncedValidate]);

  return (
    <MessageField
      id={idPrefix ? idPrefix + "-message-input" : "message-input"}
      className={className}
      label="Message (optional)"
      fullWidth
      minRows={5}
      multiline
      onChange={(event) => {
        setMessageText(prev => {
          if(messageIsDirty.current) return event.target.value;
          if(prev !== event.target.value)
          {
            messageIsDirty.current = true;
            return event.target.value;
          }
          return prev
        });
      }}
      value={messageText}
      error={!!messageError}
      helperText={messageError}
      variant="outlined"
    />
  );
}
